import { MutableRefObject, RefObject } from 'react';
import { isPossiblePhoneNumber, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import * as Yup from 'yup';
import { ValidationError } from 'yup';

import { CUSTOM } from '../constants/campaigns';
import {
  EMAIL_ADDRESS,
  ERROR_DUPLICATE_EMAIL,
  ERROR_DUPLICATE_PHONE,
  ERROR_EMAIL_INVALID,
  ERROR_EMAIL_OR_PHONE_REQUIRED,
  ERROR_PHONE_INVALID,
  ONE_LINK_STATUS,
  PHONE_NUMBER,
  URL,
} from '../constants/oneLink';
import { EMAIL_VALIDATION_REGEX } from '../constants/shell';
import { getAddressValidationSchema } from '../constants/shipping';
import { IOneLinkErrors } from '../contexts/OneLinkSummaryProvider';
import { IOrgAddress } from '../types/addressBook';
import { ICampaignSummary, ICampaignSummaryItem } from '../types/campaigns';
import {
  IOLValidationDependencies,
  IOneLink,
  IOneLinkWithCampaignSummaries,
  TOneLinkRecipient,
} from '../types/oneLink';
import { IBusinessReason } from '../types/reasons';
import { CopyableLinkTypesEnum } from '../types/shell';
import { ICommonAddress, IOneLinkShippingDetails } from '../types/shipping';
import { isAllOrgAddressesActive } from './addressBook';
import { isCampaignEligibleToOrder } from './campaigns';
import { formatDate, isDateInFuture } from './date';
import { isExistingUser } from './users';

export const getOneLinkStatusAndDate = (oneLink: IOneLink) => {
  const createdAtDate = formatDate(oneLink.created_at);

  const { label, color } = ONE_LINK_STATUS[oneLink.status];

  return {
    createdAtDate,
    label,
    color,
  };
};

export const getOneLinkCopyableInfo = (oneLink: IOneLink) => {
  const result = [];

  if (oneLink[URL]) {
    result.push({
      type: CopyableLinkTypesEnum.OneLink,
      data: {
        label: '',
        url: oneLink[URL],
      },
    });
  }

  return result;
};

export const validateRecipients = (
  recipients: TOneLinkRecipient[],
  uploadedRecipients?: TOneLinkRecipient[] | null,
  activeRecipient?: TOneLinkRecipient,
) => {
  const filteredRecipients = activeRecipient
    ? uploadedRecipients?.filter((recipient: TOneLinkRecipient) => {
        if (activeRecipient.id && recipient.id) {
          return recipient.id !== activeRecipient.id;
        }

        if (activeRecipient.uid && recipient.uid) {
          return recipient.uid !== activeRecipient.uid;
        }
        return true;
      })
    : uploadedRecipients;

  const uploadedRecipientsEmails = filteredRecipients?.map((recipient: TOneLinkRecipient) => recipient[EMAIL_ADDRESS]);
  const uploadedRecipientsPhones = filteredRecipients?.map((recipient: TOneLinkRecipient) => recipient[PHONE_NUMBER]);

  const successfulRecipients: TOneLinkRecipient[] = [];
  const failedRecipients: TOneLinkRecipient[] = [];

  const emailSet = new Set();
  const phoneSet = new Set();

  recipients?.forEach((recipient: TOneLinkRecipient) => {
    // Rule 1: email OR phone is required
    if (!hasEmailOrPhone(recipient)) {
      recipient.error = ERROR_EMAIL_OR_PHONE_REQUIRED;
      delete recipient.id;
      failedRecipients.push(recipient);
      return;
    }

    // Rule 2: email duplicates
    if (
      recipient[EMAIL_ADDRESS] &&
      (emailSet.has(recipient[EMAIL_ADDRESS]) || uploadedRecipientsEmails?.includes(recipient[EMAIL_ADDRESS]))
    ) {
      recipient.error = ERROR_DUPLICATE_EMAIL;
      delete recipient.id;
      failedRecipients.push(recipient);
      return;
    }

    // Rule 3: phone duplicates
    if (
      recipient[PHONE_NUMBER] &&
      (phoneSet.has(recipient[PHONE_NUMBER]) || uploadedRecipientsPhones?.includes(recipient[PHONE_NUMBER]))
    ) {
      recipient.error = ERROR_DUPLICATE_PHONE;
      delete recipient.id;
      failedRecipients.push(recipient);
      return;
    }

    // Rule 4: email check
    if (recipient[EMAIL_ADDRESS] && !isValidEmail(recipient[EMAIL_ADDRESS])) {
      recipient.error = ERROR_EMAIL_INVALID;
      delete recipient.id;
      failedRecipients.push(recipient);
      return;
    }

    // Rule 5: phone check
    if (recipient[PHONE_NUMBER] && !isValidPhoneNumber(recipient[PHONE_NUMBER])) {
      recipient.error = ERROR_PHONE_INVALID;
      delete recipient.id;
      failedRecipients.push(recipient);
      return;
    }

    // No errors, add to successful recipients
    delete recipient.error;
    successfulRecipients.push(recipient);

    // Add email and phone to sets for duplicate checks
    if (recipient[EMAIL_ADDRESS]) {
      emailSet.add(recipient[EMAIL_ADDRESS]);
    }

    if (recipient[PHONE_NUMBER]) {
      phoneSet.add(recipient[PHONE_NUMBER]);
    }
  });

  return { successfulRecipients, failedRecipients };
};

const trimmedEmail = (email: string) => email.replace(/^,|,$/g, '').trim();
const isValidEmail = (email: string) => new RegExp(EMAIL_VALIDATION_REGEX).test(trimmedEmail(email));

const hasEmailOrPhone = (obj: { [EMAIL_ADDRESS]: string; [PHONE_NUMBER]: string }) =>
  obj[EMAIL_ADDRESS]?.length > 0 || obj[PHONE_NUMBER]?.length > 0;

export const phoneTransform = (val: string, field: string): string => {
  if (field === PHONE_NUMBER) {
    const candidate = !val.startsWith('+') ? `+${val}` : val;

    if (isPossiblePhoneNumber(candidate)) {
      const parsedPhone = parsePhoneNumber(candidate);
      return parsedPhone?.number ? parsedPhone.number : candidate;
    }
    return val;
  }

  return val;
};

export const mapOrgAddressToCommonAddress = (address?: IOrgAddress): ICommonAddress | undefined => {
  if (!address) {
    return;
  }

  const { address1, address2, city, country, state, zip } = address;

  return { address1, address2, city, country, state, zip };
};

export const mapShippingDetailsToOneLink = (shippingDetails: IOneLinkShippingDetails) => {
  const { receiver_address, ...details } = shippingDetails;

  return {
    receiver_fixed_address: receiver_address,
    ...details,
  };
};

export const mapInstanceData = (instance: IOneLinkWithCampaignSummaries | null) => {
  if (!instance) {
    return null;
  }

  return {
    summary_info: {
      expiration_date: instance.expiration_date,
      user_id: instance.user_id,
    },
    inventory_info: {
      items: instance.items,
      pre_created_engagement_id: instance.pre_created_engagement_id,
      pick_campaign_ids: instance.pick_campaign_ids,
    },
    shipping_info: {
      receiver_fixed_address: instance.receiver_fixed_address,
      org_address_ids: instance.org_address_ids,
    },
    additional_info: {
      on_hold_until: instance.on_hold_until,
      business_reason_id: instance.business_reason_id,
    },
    security_info: {
      max_submit_count: instance.max_submit_count,
    },
  };
};

export const isBusinessReasonRequiredInOL = (campaigns: ICampaignSummary[] | undefined): boolean => {
  return campaigns?.some((c: ICampaignSummary) => c.business_reason_required) || false;
};

export const isBusinessReasonExisting = (reasons: IBusinessReason[] | null, id?: string) => {
  return reasons?.some((reason: IBusinessReason) => reason.uid === id);
};

export const isBusinessReasonValid = (
  reasons: IBusinessReason[] | null,
  id: string | undefined,
  instance: IOneLinkWithCampaignSummaries | null,
  isBusinessReasonRequiredForSingleCampaign?: boolean,
) => {
  const isPYG = instance?.pick_campaign_ids && instance?.pick_campaign_ids.length > 0;
  const isSingleCampaign = instance?.pre_created_engagement_id !== CUSTOM && !instance?.pick_campaign_ids;

  // no business reason
  if (!id) {
    if (isPYG) {
      return !isBusinessReasonRequiredInOL(instance?.pick_campaigns);
    }
    if (isSingleCampaign) {
      return !isBusinessReasonRequiredForSingleCampaign;
    }
    return true;
  }

  // business reason ID is provided, verify it exists
  const isReasonExists = reasons?.some((reason: IBusinessReason) => reason.uid === id);

  if (isPYG) {
    const isRequired = isBusinessReasonRequiredInOL(instance?.pick_campaigns);
    return !isRequired || isReasonExists;
  }

  if (isSingleCampaign) {
    return !isBusinessReasonRequiredForSingleCampaign || isReasonExists;
  }

  return true;
};

export const isItemsValid = (campaigns?: ICampaignSummary[], uid?: string, items?: ICampaignSummaryItem[] | null) => {
  if (items) {
    return isCampaignEligibleToOrder(items);
  }

  if (campaigns) {
    return campaigns.every((campaign) => isCampaignEligibleToOrder(campaign.items));
  }

  return false;
};

export const isPickCampaignIdsValid = (campaigns?: ICampaignSummary[], ids?: string[] | null) => {
  if (ids) {
    const validCampaignCount = campaigns?.filter((campaign) => isCampaignEligibleToOrder(campaign.items)).length;

    return validCampaignCount && validCampaignCount >= 2;
  }

  return false;
};

export const mapOLValidationErrors = (error: ValidationError): IOneLinkErrors => {
  // error.inner is an array of ValidationError objects in yup
  return error.inner.reduce((accumulator: IOneLinkErrors, err: ValidationError) => {
    if (err.path) {
      const path = err.path.split('.');
      let errors = accumulator as Record<string, any>;
      for (let i = 0; i < path.length; i++) {
        if (!errors[path[i]]) {
          errors[path[i]] = i === path.length - 1 ? err.message : {};
        }
        errors = errors[path[i]];
      }
    }
    return accumulator;
  }, {} as IOneLinkErrors);
};

export const mapErrorObjectToErrorList = (errors?: IOneLinkErrors) => {
  if (!errors || Object.keys(errors).length === 0) {
    return [];
  }

  return Object.values(errors).flatMap((section: IOneLinkErrors[keyof IOneLinkErrors]) =>
    typeof section === 'object' ? Object.values(section).filter(Boolean) : [],
  );
};

export const handleScrollListUpdate = (
  containerRef: RefObject<HTMLDivElement>,
  inputRef: RefObject<HTMLDivElement>,
  prevLengthRef: MutableRefObject<number>,
  nextValue: string[],
  onSet: (val: string[]) => void,
) => {
  const container = containerRef.current;

  let wasAtBottom = false;
  let previousScrollTop = 0;
  let previousScrollHeight = 0;

  if (container) {
    previousScrollTop = container.scrollTop;
    previousScrollHeight = container.scrollHeight;

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    wasAtBottom = distanceFromBottom <= 1;
  }

  const prevLength = prevLengthRef.current;

  onSet(nextValue);

  requestAnimationFrame(() => {
    const cRef = containerRef.current;
    if (!cRef) {
      return;
    }

    const currentScrollHeight = cRef.scrollHeight;
    const isScrollHeightIncreased = currentScrollHeight > previousScrollHeight;
    const isListGrowing = nextValue?.length > prevLength;

    if ((isListGrowing && !wasAtBottom) || isScrollHeightIncreased) {
      cRef.scrollTo({
        top: cRef.scrollHeight,
        behavior: 'smooth',
      });
    } else {
      cRef.scrollTop = previousScrollTop;
    }

    prevLengthRef.current = nextValue.length;

    requestAnimationFrame(() => {
      inputRef?.current?.focus();
    });
  });
};

export const createOneLinkValidationSchema = (deps: IOLValidationDependencies) => {
  const { users, reasons, instance, addressBook, isBusinessReasonRequiredForSingleCampaign } = deps;

  return Yup.object().shape({
    summary_info: Yup.object().shape({
      expiration_date: Yup.string().test('is-future-expiration', 'Expiration date is outdated', (v) => {
        return v ? isDateInFuture(v) : true;
      }),
      user_id: Yup.string().test('is-user-present', 'Sender does not exist', (id) => {
        return isExistingUser(id, users);
      }),
    }),
    additional_info: Yup.object().shape({
      on_hold_until: Yup.string().test('is-future-on-hold', 'On Hold date is outdated', (v) => {
        return v ? isDateInFuture(v) : true;
      }),
      business_reason_id: Yup.string().test('is-business-reason-required', 'Business Reason is required', (v) => {
        return isBusinessReasonValid(reasons, v, instance, isBusinessReasonRequiredForSingleCampaign);
      }),
    }),
    security_info: Yup.object().shape({
      max_submit_count: Yup.number().test(
        'is-max-less-than-use',
        'Redemption limit is less than count of available sends',
        (v) => {
          if (v && instance?.use_times_count) {
            return v > instance.use_times_count;
          }
          return true;
        },
      ),
    }),
    inventory_info: Yup.object().shape({
      items: Yup.array()
        .nullable()
        .test('is-items-valid', 'Out-of-stock items', (v) => {
          if (v === null) {
            return true;
          }
          return !!isItemsValid(instance?.pick_campaigns, instance?.pre_created_engagement_id, instance?.items);
        }),
      pick_campaign_ids: Yup.array()
        .nullable()
        .test('is-pick-campaign-ids-valid', 'Less than 2 valid campaigns', (v) => {
          if (v === null) {
            return true;
          }
          return !!isPickCampaignIdsValid(instance?.pick_campaigns, instance?.pick_campaign_ids);
        }),
    }),
    shipping_info: Yup.object().shape({
      receiver_fixed_address: Yup.object()
        .nullable()
        .test('receiver-valid', 'Receiver address is invalid', (address, context) => {
          const parent = context.options.context || context.parent;
          const isFixed = parent?.is_receiver_address_fixed;
          const hasAddress = address && Object.keys(address).length > 0;
          if (!isFixed && !hasAddress) {
            return true;
          }
          if (!hasAddress) {
            return false;
          }
          try {
            const addressValidationSchema = getAddressValidationSchema({ strict: true });
            addressValidationSchema.validateSync(address, { abortEarly: false });
            return true;
          } catch (error) {
            return false;
          }
        }),
      org_address_ids: Yup.array()
        .nullable()
        .test('org-addresses-active', 'One or more selected organization addresses are inactive', (ids) => {
          if (!ids || ids.length === 0) {
            return true;
          }
          return isAllOrgAddressesActive(addressBook, ids);
        }),
      is_receiver_address_fixed: Yup.boolean(),
    }),
  });
};
